"use client"

import React from 'react'
import { UIButton, type UIButtonProps } from './ui-button'
import { UITypography } from '../display/ui-typography'
import { Copy, Check } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface UIButtonCopyProps extends Omit<UIButtonProps, 'children' | 'onClick' | 'onCopy'> {
  value: string
  onCopy?: (value: string) => void
  children?: React.ReactNode
  /**
   * Content to show when copied (replaces children)
   */
  copiedChildren?: React.ReactNode
  /**
   * Custom className for icon (e.g., "h-3 w-3")
   * @default "h-4 w-4"
   */
  iconClassName?: string
  /**
   * Additional className applied when copied
   */
  copiedClassName?: string
  /**
   * Accessible label for icon-only mode (when no children provided)
   * @default "Copy to clipboard" / "Copied to clipboard"
   */
  'aria-label'?: string
}

/**
 * Copy Button Component
 * Based on common copy-to-clipboard patterns
 * Use for copying text, code, URLs, or other content to clipboard
 */
export const UIButtonCopy = React.forwardRef<HTMLButtonElement, UIButtonCopyProps>(
  ({
    value,
    onCopy,
    children,
    copiedChildren,
    iconClassName = "h-4 w-4",
    copiedClassName,
    variant = "outline",
    size = "sm",
    className,
    'aria-label': ariaLabel,
    ...props
  }, ref) => {
    const [copied, setCopied] = React.useState(false)

    const handleCopy = async (e: React.MouseEvent) => {
      e.stopPropagation() // 이벤트 버블링 방지
      try {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        onCopy?.(value)

        setTimeout(() => {
          setCopied(false)
        }, 2000)
      } catch (err) {
        console.error('Failed to copy text: ', err)
      }
    }

    // Provide automatic accessibility for icon-only mode
    const defaultAriaLabel = copied ? "Copied to clipboard" : "Copy to clipboard"
    const finalAriaLabel = !(children || copiedChildren) ? (ariaLabel || defaultAriaLabel) : ariaLabel

    // Combine className with copiedClassName when copied
    const combinedClassName = cn(className, copied && copiedClassName)

    // Determine which content to show
    const displayChildren = copied && copiedChildren ? copiedChildren : children

    return (
      <UIButton
        variant={variant}
        size={size}
        onClick={handleCopy}
        aria-label={finalAriaLabel}
        className={combinedClassName}
        ref={ref}
        {...props}
      >
        {copied ? <Check className={iconClassName} /> : <Copy className={iconClassName} />}
        {displayChildren && <UITypography as="span" variant="p" size="sm" className="ml-2">{displayChildren}</UITypography>}
      </UIButton>
    )
  }
)

UIButtonCopy.displayName = "UIButtonCopy"
